import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Filter, IToolsService } from '@huge-monorepo/hip/domain';
import * as XLSX from 'xlsx';
import { google } from 'google-maps';

@Injectable({
  providedIn: 'root',
})
export class ToolsService implements IToolsService {
  FileNameRegex: string = "^[a-zA-Z0-9!@#()_$-+= {}']*$";
  maxCSQ: number = 5;
  invalidFilepathCharacters = '~ , " , # , % , & , * , : , < , > , ? , / , \\ , | , { , }';
  invalidFilepathNote = `Note: Job number and inventory number can't contain ${this.invalidFilepathCharacters}`;

  constructor() { }

  GetFileNameRegex(): string {
    return this.FileNameRegex;
  }

  CreateInterval(minutes: number, func: Function, scope: any): any {
    return setInterval(() => {
      if (func != null) {
        func(scope);
      }
    }, minutes * 1000 * 60);
  }

  DestroyInterval(inter: any) {
    if (inter != null) {
      clearInterval(inter);
    }
  }

  GetZeroArray(len: number): any[] {
    const arr = [];
    for (let i = 0; i < len; i++) arr.push(0);
    return arr;
  }

  CalculateUsages(usages, rounding = 0) {
    const calculatedUsages = this.GetZeroArray(usages.length);
    for (let i = 0; i < usages.length; i++) {
      if (i == 0) {
        calculatedUsages[0] = usages[0].toFixed(rounding);
      } else {
        const temp = parseFloat(calculatedUsages[i - 1]) + usages[i];
        calculatedUsages[i] = temp.toFixed(rounding);
      }
    }
    return calculatedUsages;
  }

  CalculateUsageLevel = (total, usage: number): string => {
    const perc = (total / usage) * 100;
    if (!Number.isNaN(perc) && perc > 0) {
      if (perc < 25) {
        return 'Low';
      } else if (perc < 75) {
        return 'Moderate';
      } else if (perc <= 100) {
        return 'High';
      } else {
        return 'Extreme';
      }
    }
    return 'No';
  };

  FormatUsage(bytes: number): string {
    const unit = 1000.0;
    if (bytes < unit) {
      return bytes + ' B';
    }
    let div = unit;
    let exp = 0;
    for (let i = bytes / unit; i >= unit; i /= unit) {
      div *= unit;
      exp++;
    }
    return (Math.round((bytes / div) * 100) / 100).toFixed(2) + ' ' + 'kMGTPE'[exp] + 'B';
  }

  StringToBytesWithObject = (obj: Object, value: string): string => {
    return this.StringToBytes(value);
  };

  StringToBytes = (value: string): string => {
    const bytes = Number(value);

    if (bytes > Math.pow(1000, 4)) {
      return String(bytes / Math.pow(1000, 4)) + ' T';
    } else if (bytes > Math.pow(1000, 3)) {
      return String(bytes / Math.pow(1000, 3)) + ' G';
    } else if (bytes > Math.pow(1000, 2)) {
      return String(bytes / Math.pow(1000, 2)) + ' M';
    } else if (bytes > 1000) {
      return String(bytes / 1000) + ' K';
    }
    return value + ' B';
  };

  RandomRGBA(r: number, g: number, b: number) {
    var o = Math.round,
      rand = Math.random;
    return 'rgba(' + o(rand() * r) + ',' + o(rand() * g) + ',' + o(rand() * b) + ',' + rand().toFixed(1) + ')';
  }

  RandomNumber(r: number) {
    var o = Math.round,
      rand = Math.random;
    return o(o(rand() * r) + rand());
  }

  GenerateRandomColor(): string {
    return this.RandomRGBA(Math.random() * 240 + 10, Math.random() * 240 + 10, Math.random() * 240 + 10);
  }

  RemoveQuotes = (value: string): string => {
    return value != null ? value.replace(/['"]+/g, '') : '-';
  };

  RemoveExtension = (obj: Object, value: string): string => {
    if (value != null) {
      let parts = value.split('.');
      if (parts && parts.length > 0) {
        return parts[0];
      }
    }
    return value;
  };

  GetStartOfMonth(date: Date): Date {
    let newDate = new Date(date.getFullYear(), date.getMonth(), 1);
    newDate.setHours(0, 0, 0);
    return newDate;
  }

  CopyToClipboard(value: string) {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = value;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  GetTimeFilters(): Observable<Filter[]> {
    return of([
      { iD: 1, name: 'Minute' },
      { iD: 2, name: '5 Minutes' },
      { iD: 3, name: '30 Minutes' },
      { iD: 4, name: 'Hour' },
      { iD: 5, name: 'Day' },
      { iD: 6, name: 'Week' },
      { iD: 7, name: 'Month' },
    ]);
  }

  GetTimeFiltersLargeDataSet(): Observable<Filter[]> {
    return of([
      { iD: 4, name: 'Hour' },
      { iD: 5, name: 'Day' },
      { iD: 6, name: 'Week' },
      { iD: 7, name: 'Month' },
    ]);
  }

  FormatDateTime(d: any): string {
    let dformat =
      [d.getFullYear().padLeft(), (d.getMonth() + 1).padLeft(), d.getDate()].join('-') +
      ' ' +
      [d.getHours().padLeft(), d.getMinutes().padLeft(), d.getSeconds().padLeft()].join(':');
    return dformat;
  }

  FormatDate(d: any): string {
    let dformat = [d.getFullYear().padLeft(), (d.getMonth() + 1).padLeft(), d.getDate()].join('-');
    return dformat;
  }

  getMonthYearFromDate(d: any): string {
    let dformat = [(d.getMonth() + 1).padLeft(), d.getFullYear().padLeft()].join('/');
    return dformat;
  }

  FormatFloat(val: number, decimals: number = 2): null | string {
    if (val == null || typeof val !== 'number') {
      return null;
    }

    return val.toFixed(decimals);
  }

  ExtractPaginationFromHeaders(headers): number {
    let pagination = JSON.parse(headers.get('x-pagination'));
    if (pagination != null && pagination['TotalCount'] >= 0) {
      return parseInt(pagination['TotalCount']);
    }
    return 0;
  }

  static ValidateRange(range: string, input) {
    let regex = /^((([0-9]+-[0-9]+)|[0-9]+),)*(([0-9]+-[0-9]+)|[0-9]+)$/;
    let passed = regex.test(range);
    if (!passed) {
      input.target.invalid = true;
    }
  }

  static BuildURLParameters = (url: string, obj: any, checkForExisting: Boolean = false) => {
    if (obj == null || (Object.keys(obj).length === 0 && obj.constructor === Object)) {
      return url;
    }

    let obkeys = [];
    let obvals = [];
    if (checkForExisting) {
      let temp = url.split('?');

      if (temp.length > 1) {
        let tempKeyVals = temp[1].split('&');
        tempKeyVals.forEach((el) => {
          let k = el.split('=');
          obkeys.push(k[0]);
          obvals.push(k[1]);
        });
      }
      url = temp[0];
    }

    for (let key in obj) {
      if (obj[key] === null || obj[key] == undefined) {
        continue;
      }
      obkeys.push(key);
      switch (typeof obj[key]) {
        case 'string':
          obvals.push(obj[key]);
          break;
        case 'number':
          obvals.push(obj[key].toString());
          break;
        case 'boolean':
          obvals.push(obj[key] === true ? 'true' : 'false');
          break;
        default:
        // console.log("Defaulting with Type: ", typeof(obj[key]));
      }
    }

    if (obkeys.length > 0) {
      url += '?';
      for (let i = 0; i < obkeys.length; i++) {
        if (i != 0) {
          url += '&';
        }
        url += obkeys[i] + '=' + obvals[i];
      }
    }

    return url;
  };

  public convertToEndOfDay(oldFormat: string): string {
    let theDate: Date = new Date(oldFormat);
    theDate.setDate(theDate.getDate() + 1);
    theDate.setHours(1, 59, 59, 999);
    return theDate.toISOString();
  }

  FormatStartDate = (oldFormat: string): string => {
    const theDate: Date = new Date(oldFormat);
    theDate.setHours(2, 0, 0, 0);
    return theDate.toISOString();
  };

  FormatEndDate = (oldFormat: string): string => {
    const theDate: Date = new Date(oldFormat);
    theDate.setDate(theDate.getDate() + 1);
    theDate.setHours(1, 59, 59, 999);
    return theDate.toISOString();
  };

  GetEndOfMonthFromDate(d: Date): Date {
    let today = d;
    let date = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    date.setUTCHours(23, 59, 59);
    return date;
  }

  public IsValidFileName(str: string): Boolean {
    let regex = /^[a-zA-Z0-9!@#()_$\-+= {}']*$/;
    return regex.test(str);
  }

  public ReplaceLineBreaksWithHTMLBreaks(str) {
    return str.replace(/(?:\r\n|\r|\n)/g, '<br/>');
  }

  public static CreatePaginationQuery(count: number, offset: number, search: string, orderBy?: string, desc?: boolean): string {
    return ToolsService.BuildURLParameters(
      '',
      {
        orderBy: orderBy,
        desc: desc,
        search: search,
        count: count,
        offset: offset,
      },
      false,
    );
  }

  ParseAPIDate = (date: string): Date => {
    let newDate = new Date();
    if (!date) return newDate;
    newDate.setUTCFullYear(parseInt(date.substr(0, 4)));
    newDate.setUTCMonth(parseInt(date.substr(5, 2)) - 1);
    newDate.setUTCDate(parseInt(date.substr(8, 2)));
    newDate.setUTCHours(parseInt(date.substr(11, 2)));
    newDate.setUTCMinutes(parseInt(date.substr(14, 2)));
    newDate.setUTCSeconds(parseInt(date.substr(17, 2)));
    return newDate;
  };

  FormatDateTimeFromDate(date: Date): string {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  FormatDateTimeFromDateSwedish(date: Date): string {
    return date.toLocaleDateString('sv-SE') + ' ' + date.toLocaleTimeString('sv-SE');
  }

  FormatDateFromDate(date: Date): string {
    return date.toLocaleDateString('sv-SE');
  }

  ////////////////////////////////////////////////// START OF MAP STUFF //////////////////////////////////////////////////
  GetBasicTowerMarkerInfo(mnc: number, type: any, state: boolean = null, checkState: boolean = false): any {
    let markerInfo = {
      icon: null,
      name: null,
    };

    let switchVal = ``;

    if (checkState) {
      if (state != null) {
        // Either Online or Offline
        if (state == true) {
          // Online
          switchVal += `${mnc}online`;
        } else {
          // Offline
          switchVal += `${mnc}offline`;
        }
      } else {
        // Pending status
        switchVal += `${mnc}pending`;
      }
    } else {
      switchVal = `${mnc}${type}`;
    }

    let splitSwitch = [`${mnc}`, `${type}`];
    switch (true) {
      // VODACOM
      case switchVal == '1a': // Vodacom Accepted
        markerInfo.icon = 'VodacomAcceptedMarker.png';
        markerInfo.name = 'Vodacom';
        break;
      case switchVal == '1p': // Vodacom Previous
        markerInfo.icon = 'VodacomPreviousMarker.png';
        markerInfo.name = 'Vodacom';
        break;
      case switchVal == '1c': // Vodacom Current
        markerInfo.icon = 'VodacomCurrentMarker.png';
        markerInfo.name = 'Vodacom';
        break;
      case switchVal == '1null': // Vodacom
        markerInfo.icon = 'VodacomMarker.png';
        markerInfo.name = 'Vodacom';
        break;
      case switchVal == '1pending': // Vodacom Pending
        markerInfo.icon = 'VodacomPendingMarker.png';
        markerInfo.name = 'Vodacom';
        break;
      case switchVal == '1online': // Vodacom Online
        markerInfo.icon = 'VodacomOnlineMarker.png';
        markerInfo.name = 'Vodacom';
        break;
      case switchVal == '1offline': // Vodacom Offline
        markerInfo.icon = 'VodacomOfflineMarker.png';
        markerInfo.name = 'Vodacom';
        break;
      // TELKOM
      case switchVal == '2a': // Telkom Accepted
      case switchVal == '5a': // Telkom Accepted
        markerInfo.icon = 'TelkomAcceptedMarker.png';
        markerInfo.name = 'Telkom';
        break;
      case switchVal == '2p': // Telkom Previous
      case switchVal == '5p': // Telkom Previous
        markerInfo.icon = 'TelkomPreviousMarker.png';
        markerInfo.name = 'Telkom';
        break;
      case switchVal == '2c': // Telkom Current
      case switchVal == '5c': // Telkom Current
        markerInfo.icon = 'TelkomCurrentMarker.png';
        markerInfo.name = 'Telkom';
        break;
      case switchVal == '2null': // Telkom
      case switchVal == '5null': // Telkom
        markerInfo.icon = 'TelkomMarker.png';
        markerInfo.name = 'Telkom';
        break;
      case switchVal == '5pending': // Telkom Pending
      case switchVal == '2pending': // Telkom Pending
        markerInfo.icon = 'TelkomPendingMarker.png';
        markerInfo.name = 'Telkom';
        break;
      case switchVal == '2online': // Telkom Online
      case switchVal == '5online': // Telkom Online
        markerInfo.icon = 'TelkomOnlineMarker.png';
        markerInfo.name = 'Telkom';
        break;
      case switchVal == '2offline': // Telkom Offline
      case switchVal == '5offline': // Telkom Offline
        markerInfo.icon = 'TelkomOfflineMarker.png';
        markerInfo.name = 'Telkom';
        break;
      // CELL C
      case switchVal == '7a': // Cell C Accepted
        markerInfo.icon = 'CellCAcceptedMarker.png';
        markerInfo.name = 'Cell C';
        break;
      case switchVal == '7p': // Cell C Previous
        markerInfo.icon = 'CellCPreviousMarker.png';
        markerInfo.name = 'Cell C';
        break;
      case switchVal == '7c': // Cell C Current
        markerInfo.icon = 'CellCCurrentMarker.png';
        markerInfo.name = 'Cell C';
        break;
      case switchVal == '7null': // Cell C
        markerInfo.icon = 'CellCMarker.png';
        markerInfo.name = 'Cell C';
        break;
      case switchVal == '7pending': // Cell C Pending
        markerInfo.icon = 'CellCPendingMarker.png';
        markerInfo.name = 'Cell C';
        break;
      case switchVal == '7online': // Cell C Online
        markerInfo.icon = 'CellCOnlineMarker.png';
        markerInfo.name = 'Cell C';
        break;
      case switchVal == '7offline': // Cell C Offline
        markerInfo.icon = 'CellCOfflineMarker.png';
        markerInfo.name = 'Cell C';
        break;
      // MTN
      case switchVal == '10a': // MTN Accepted
      case switchVal == '12a': // MTN Accepted
        markerInfo.icon = 'MTNAcceptedMarker.png';
        markerInfo.name = 'MTN';
        break;
      case switchVal == '10p': // MTN Previous
      case switchVal == '12p': // MTN Previous
        markerInfo.icon = 'MTNPreviousMarker.png';
        markerInfo.name = 'MTN';
        break;
      case switchVal == '10c': // MTN Current
      case switchVal == '12c': // MTN Current
        markerInfo.icon = 'MTNCurrentMarker.png';
        markerInfo.name = 'MTN';
        break;
      case switchVal == '10null': // MTN
      case switchVal == '12null': // MTN
        markerInfo.icon = 'MTNMarker.png';
        markerInfo.name = 'MTN';
        break;
      case switchVal == '10pending': // MTN Pending
      case switchVal == '12pending': // MTN Pending
        markerInfo.icon = 'MTNPendingMarker.png';
        markerInfo.name = 'MTN';
        break;
      case switchVal == '10online': // MTN Online
      case switchVal == '12online': // MTN Online
        markerInfo.icon = 'MTNOnlineMarker.png';
        markerInfo.name = 'MTN';
        break;
      case switchVal == '10offline': // MTN Offline
      case switchVal == '12offline': // MTN Offline
        markerInfo.icon = 'MTNOfflineMarker.png';
        markerInfo.name = 'MTN';
        break;
      // OTHER MNC
      case splitSwitch[0] !== 'null' && splitSwitch[1] == 'a': // Other Accepted
        markerInfo.icon = 'OtherAcceptedMarker.png';
        markerInfo.name = 'Other';
        break;
      case splitSwitch[0] !== 'null' && splitSwitch[1] == 'p': // Other Previous
        markerInfo.icon = 'OtherPreviousMarker.png';
        markerInfo.name = 'Other';
        break;
      case splitSwitch[0] !== 'null' && splitSwitch[1] == 'c': // Other Current
        markerInfo.icon = 'OtherCurrentMarker.png';
        markerInfo.name = 'Other';
        break;

      case splitSwitch[0] !== 'null' && splitSwitch[1] == 'null': // Other
        markerInfo.icon = 'OtherMarker.png';
        markerInfo.name = 'Other';
        break;

      // GENERIC
      case switchVal == 'nulla': // Generic Accepted
        markerInfo.icon = 'GenericAcceptedMarker.png';
        markerInfo.name = 'Generic';
        break;
      case switchVal == 'nullp': // Generic Previous
        markerInfo.icon = 'GenericPreviousMarker.png';
        markerInfo.name = 'Generic';
        break;
      case switchVal == 'nullc': // Generic Current
        markerInfo.icon = 'GenericCurrentMarker.png';
        markerInfo.name = 'Generic';
        break;

      // DEFAULT
      default:
        markerInfo.icon = 'DefaultMarker.png';
        markerInfo.name = 'Default';
        break;
    }
    return markerInfo;
  }

  GetNetworkNameByMNC(mnc: number): string {
    switch (mnc) {
      case 1: // Vodacom
        return 'Vodacom';
      case 2: // Telkom
      case 5: // Telkom
        return 'Telkom';
      case 7: // Cell C
        return 'Cell C';
      case 10: // MTN
      case 12: // MTN
        return 'MTN';
      default:
        return 'Other';
    }
  }

  CalculateRadiusDistanceBasedOnCSQ(csq: number, originalDistance: number): number {
    if (originalDistance == null) {
      return 0;
    }

    let distance = originalDistance; //(range == null ? originalDistance : range);

    if (csq >= this.maxCSQ) {
      csq = this.maxCSQ;
    }

    if (csq == null || csq == 0) {
      return distance;
    }

    return (this.invertSignalStrength(csq) / this.maxCSQ) * distance;
  }

  invertSignalStrength(csq: number): number {
    switch (csq) {
      case 1:
        return 5;
      case 2:
        return 4;
      case 3:
        return 3;
      case 4:
        return 2;
      case 5:
        return 1;
      default:
        return csq;
    }
  }

  GetNetworkColourByMNC(mnc: number): string {
    switch (mnc) {
      case 1: // Vodacom
        // return "#FA8072";
        return '#EB1900';
      case 2: // Telkom
      case 5: // Telkom
        return '#166BCE';
      case 7: // Cell C
        return '#000000';
      case 10: // MTN
      case 12: // MTN
        return '#F3DB00';
      default:
        return '#666666';
    }
  }

  ////////////////////////////////////////////////// END OF MAP STUFF //////////////////////////////////////////////////

  ConvertEpochToDate(epoch: number): Date {
    var d = new Date(0); // The 0 there is the key, which sets the date to the epoch
    d.setUTCSeconds(epoch);
    return d;
  }

  ConvertDateToEpoch(d: Date): number {
    return Math.round(d.getTime() / 1000);
  }

  ParseAPIDateWithTimezone = (date: string): Date => {
    let newDate = new Date();
    newDate.setFullYear(parseInt(date.slice(0, 4)));
    newDate.setMonth(parseInt(date.slice(5, 7)) - 1);
    newDate.setDate(parseInt(date.slice(8, 10)));
    newDate.setHours(parseInt(date.slice(11, 13)));
    newDate.setMinutes(parseInt(date.slice(14, 16)));
    newDate.setSeconds(parseInt(date.slice(17, 19)));
    return newDate;
  };

  GetMaxCSQ(): number {
    return this.maxCSQ;
  }

  GetEndOfMonth(): Date {
    let today = new Date();
    let date = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    date.setUTCHours(23, 59, 59);
    return date;
  }

  parseISOString(s) {
    var b = s.split(/\D+/);
    return new Date(Date.UTC(b[0], --b[1], b[2], b[3], b[4], b[5], b[6]));
  }

  // Converts value to an accounting string type, eg. "69420" => "69 420"
  AccountingStringType(value): string {
    if (typeof value != 'string') {
      value = value.toString();
    }

    return value.split(/(?=(?:...)*$)/).join(' ');
  }

  filepathPatternValidator(c) {
    let pattern = /[~"#%&*:<>?/\{|}]+/gm;

    return pattern.test(c.value) ? { filepathPatternValidator: { valid: false } } : null;
  }

  filepathPatternBooleanWrapper(c: string) {
    let valid = this.filepathPatternValidator({ value: c });

    return valid == null ? true : false;
  }

  getLatestDate(...args): any {
    let date: Date;
    date = args.reduce((a, b) => {
      return this.transform(a) > this.transform(b) ? a : b;
    });
    return date;
  }

  /**
   * @ngService {@link ToolsService}
   * @description Formats a date
   * @param value Accepts any value that is to be transformed into a date
   * @returns Returns a date object
   */
  transform(value: any): any {
    return new Date(value);
  }

  /**
   * @ngService {@link ToolsService}
   * @description Formats a date for display purposes
   * @param value Accepts any value that is to be transformed into a date
   * @returns Returns a formatted date string
   *
   *
   */
  transformDateForDisplay(value: any): any {
    return formatDate(value, 'dd-MM-yyyy HH:mm', 'en-GB', '+0000');
  }

  /**
   * @ngService {@link ToolsService}
   * @description Gets the max numberic value of an inform type before it is considered offline
   * @param type Type of action performed, ex: ping, snapshot (not case sensitive)
   * @returns Returns a numeric value
   */
  getRouterInformTypes(type: string): number {
    type = type.toLowerCase();

    switch (type) {
      case 'acs':
        return 60;
      case 'ping':
        return 10;
      case 'snapshot':
        return 1440;
      default:
        return null;
    }
  }

  /**
   * @ngService {@link ToolsService}
   * @description Gets the string representation of the calculated status, ex: 'Online'
   * @param lastInform Last known date a device performed an action
   * @param type Type of action performed, ex: ping, snapshot (not case sensitive)
   * @returns Returns a string value
   */
  checkRouterStatus(lastInform: any, type: string): string {
    if (lastInform == null || lastInform == '') {
      return 'Unknown';
    }
    let min = this.getRouterInformTypes(type);
    let max = min + 5;
    let date = this.transform(lastInform);
    let timeDiff = Math.round((Date.now() - date) / 1000 / 60); // Time Diff in minutes
    if (timeDiff < min) {
      return 'Online';
    } else if (timeDiff <= max) {
      return 'Awaiting';
    } else if (timeDiff > max) {
      return 'Offline';
    }
    return null;
  }

  /**
   * @ngService {@link ToolsService}
   * @description Gets the boolean value of the specified status type
   * @param status The name of the status of the inform type (not case sensitive)
   * @returns A boolean value
   */
  checkRouterBoolStatus(status: string): boolean {
    status = status.toLowerCase();
    switch (status) {
      case 'online':
        return true;
      case 'awaiting':
        return true;
      case 'offline':
        return false;
      default:
        return null;
    }
  }

  /**
   * Checks if passed in date falls within the passed in months from beginning of current month
   * @param {Date} date Date that needs to be checked
   * @param {number} timeInPast Amount of months to check date on
   *
   * @return {boolean} Returns a boolean which is false if the date is within the months passed in
   */
  checkPastDate(date: Date, timeInPast: number): boolean {
    let currentDate = new Date();
    currentDate.setDate(1);
    currentDate.setMonth(currentDate.getMonth() - timeInPast);

    return date < currentDate;
  }

  calculateProjectionData(graphObject) {
    let tempDataUsed = graphObject['dataUsed'];
    let average = 0.0;
    let daysInMonth;

    if (graphObject.isCumulative != null && graphObject.isCumulative) {
      for (let i = 0; i < tempDataUsed.length; i++) {
        if (i > 0) {
          average += parseFloat(tempDataUsed[i]) - parseFloat(tempDataUsed[i - 1]);
        } else {
          average += parseFloat(tempDataUsed[i]);
        }
      }
    } else {
      for (let i = 0; i < tempDataUsed.length; i++) {
        average += parseFloat(tempDataUsed[i]);
      }
    }

    average = average / parseFloat(tempDataUsed.length);

    let now = graphObject.NewDate ? graphObject.NewDate : new Date();
    daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    for (let i = 0; i < daysInMonth; i++) {
      graphObject.projected[i] = (average * (i + 1)).toFixed(2);
    }

    for (let i = tempDataUsed.length; i < daysInMonth; i++) {
      let parseDate = new Date(new Date().setDate(i + 1));
      graphObject['weeks'].push(this.FormatDateFromDate(parseDate));
    }

    return { graphObject: graphObject, daysInMonth: daysInMonth };
  }

  GetCurrentMonthName = (): string => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const d = new Date();
    return monthNames[d.getMonth()];
  };

  GetCurrentYear = (): string => {
    const d = new Date();
    return d.getFullYear().toString();
  };

  GetDateMonthName = (d: Date): string => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return monthNames[d.getMonth()];
  };

  GetDateYear = (d: Date): string => {
    return d.getFullYear().toString();
  };

  ConvertMinutesToHours(value: number) {
    let num = value;
    if (num > 60) {
      let hours = num / 60;
      let rhours = Math.floor(hours);
      let minutes = (hours - rhours) * 60;
      let rminutes = Math.round(minutes);
      return `${rhours} hour${rhours > 1 ? 's' : ''}, ${rminutes} minute${rminutes > 1 ? 's' : ''}`;
    }
    return `${num} minute${num > 1 ? 's' : ''}`;
  }

  GetStartOfDay(d: Date): Date {
    let newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    newDate.setHours(2, 0, 0, 0);
    return newDate;
  }

  GetEndOfDay(d: Date): Date {
    let newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(1, 59, 59, 999);
    return newDate;
  }

  getDateFromString(stringDate: string) {
    let date = new Date(stringDate);
    let offset = date.getTimezoneOffset();
    date.setMinutes(date.getMinutes() + offset);
    return date;
  }

  /**
   * @description Converts seconds to hh:mm:ss
   * @param value
   * @returns
   */
  convertHMS(value) {
    const sec = parseInt(value); // convert value to number if it's string
    let hours = Math.floor(sec / 3600); // get hours
    let minutes = Math.floor((sec - hours * 3600) / 60); // get minutes
    let seconds = sec - hours * 3600 - minutes * 60; //  get seconds
    return (hours < 10 ? '0' + hours : hours) + ':' + (minutes < 10 ? '0' + minutes : minutes) + ':' + (seconds < 10 ? '0' + seconds : seconds); // Return is HH : MM : SS
  }

  /**
   * @description Gets the pagination headers from a get response or a post and observe response
   * @param response The response object
   * @returns The relevant pagination object if it exists
   */
  getPaginationHeaders(response: any): any {
    let paginationString = response['headers'].get('x-pagination');
    if (paginationString == null) {
      return null;
    }
    return JSON.parse(paginationString) as any;
  }

  calculateTimeDifference(startDateTime: any, endDateTime: any) {
    let date1 = new Date(startDateTime).getTime();
    let date2 = new Date(endDateTime).getTime();
    let time = date2 - date1;
    let hoursDiff = time / (3600 * 1000);
    return hoursDiff;
  }

  DownloadExcel(data: any[], filename: string): void {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.xlsx`;
    link.click();
  }

  DownloadCsv(objects: any[], fileName: string): void {
    const header = Object.keys(objects[0]).join(',') + '\n';
    const data = objects.map((obj) => Object.values(obj).join(',') + '\n').join('');
    const csvContent = 'data:text/csv;charset=utf-8,' + header + data;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  GetSimIconBasedOnNetworkName(netName: string): string {
    let icon = '';
    switch (netName) {
      case 'Telkom Mobile':
        icon = 'blue';
        break;
      case 'SA - Vodacom':
      case 'LES - Vodacom':
        icon = 'red';
        break;
      case 'SA - Cell C':
        icon = 'black';
        break;
      case 'ZAM - MTN':
      case 'SA - MTN':
      case 'SWZ - MTN':
        icon = 'yellow';
        break;
      default:
        icon = 'grey';
        break;
    }
    return icon + '-sim.png';
  }

  ValueToGigabyte(value: number): number {
    return value * 1000 * 1000 * 1000;
  }



  BytesConversion(value: number, to: "b" | "mb" | "kb" | "gb" | "tb"): number {
    let convertedValue: number;

    switch (to) {
      case "b":
        convertedValue = value;
        break;
      case "kb":
        convertedValue = value / 1_000;
        break;
      case "mb":
        convertedValue = value / 1_000_000;
        break;
      case "gb":
        convertedValue = value / 1_000_000_000;
        break;
      case "tb":
        convertedValue = value / 1_000_000_000_000;
        break;
    }

    return +(convertedValue.toFixed(2));
  }

  handleZoom(event: WheelEvent, map: google.maps.Map): void {
    event.preventDefault(); // Prevent default zooming
    map.setOptions({isFractionalZoomEnabled: true});

    if (map) {
      const currentZoom = map.getZoom() || 0;
      // Custom zoom logic
      const zoomStep = 0.1
      if (event.deltaY < 0) {
        map.setZoom(currentZoom + zoomStep); // Zoom in
      } else if (event.deltaY > 0) {
        map.setZoom(currentZoom - zoomStep); // Zoom out
      }
    }
  }
}
